type LoadScriptOptions = {
    src: string;
    id: string;
    async?: boolean;
};

const loadedScripts = new Set<string>();
const loadingPromises = new Map<string, Promise<void>>();

export const shouldSkipVendorScripts =
    import.meta.env.MODE === 'test' ||
    import.meta.env.VITE_REACT_APP_IS_EXTENSION === 'true';

export function isScriptLoaded(id: string): boolean {
    return loadedScripts.has(id);
}

export function loadVendorScript(options: LoadScriptOptions): Promise<void> {
    const { src, id, async = true } = options;

    if (shouldSkipVendorScripts) {
        return Promise.resolve();
    }

    if (loadedScripts.has(id)) {
        return Promise.resolve();
    }

    const existingPromise = loadingPromises.get(id);
    if (existingPromise) {
        return existingPromise;
    }

    const existingScript = document.getElementById(id);
    if (existingScript) {
        loadedScripts.add(id);
        return Promise.resolve();
    }

    const promise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.id = id;
        script.src = src;
        script.async = async;

        script.addEventListener('load', () => {
            loadedScripts.add(id);
            loadingPromises.delete(id);
            resolve();
        });

        script.addEventListener('error', () => {
            loadingPromises.delete(id);
            script.remove();
            reject(new Error(`Failed to load script: ${src}`));
        });

        document.body.appendChild(script);
    });

    loadingPromises.set(id, promise);
    return promise;
}
